import React from 'react';
import styled, { css } from 'styled-components/macro';

import LoadingBar from 'components/styled/LoadingBar/LoadingBar';

const StyledHeader = styled('header')<{
  inView?: boolean;
  gradientColor?: string;
  hasStickyTopBar?: boolean;
  withVerticalBackground?: boolean;
  height?: string;
}>`
  position: relative;
  // z-index was changed from 2 to 10000 to cover tooltips of TopicFilters with fixed Header
  z-index: ${({ inView }): number => (!inView ? 10000 : 2)};
  ${({ gradientColor, theme }): any =>
    !gradientColor
      ? css`
          background: linear-gradient(
            90deg,
            ${theme.HorizontalBlue},
            ${theme.HorizontalGreen}
          );
        `
      : gradientColor &&
        gradientColor === 'blue' &&
        css`
          background: linear-gradient(
            90deg,
            ${theme.GradientDarkBlue},
            ${theme.GradientLightBlue}
          );
        `}

  ${({ height }): string => (height ? `height: ${height};` : '')}
  

  /** Reserve space for the topbar if it is sticky. */
  margin-top: ${({ hasStickyTopBar, theme }): number | string =>
    hasStickyTopBar ? theme.topbarHeight : 0};

  .errorWrapper {
    color: white;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: -1;

    ${({ withVerticalBackground, theme }): any =>
      withVerticalBackground &&
      css`
        background: linear-gradient(
          180deg,
          ${theme.VerticalTop},
          ${theme.VerticalBottom}
        );
      `}
  }
`;

const LoadingBarWrapper = styled('div')`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

interface IHeaderProps {
  /** Accepts React components, no normal HTML elements */
  children: React.ReactNode;
  /** Should we show a loading state? */
  loading?: boolean;
  /** Should the TopBar be sticky or not */
  hasStickyTopBar?: boolean;
  /** To control a visibility of Header */
  inView?: boolean;
  gradientColor?: 'blue'; // to add variants in future
  withVerticalBackground?: boolean;
  entityTitle?: string;
  isMobileScreen?: boolean;
}

/** Header component with a gradient. It is wrapped in an Observer component which
 * checks if the Header is inView and passes this along to its children. */

class Header extends React.Component<IHeaderProps> {
  render(): JSX.Element {
    const {
      children,
      loading = false,
      hasStickyTopBar = false,
      inView,
      gradientColor,
      withVerticalBackground,
      isMobileScreen,
      entityTitle,
    } = this.props;

    let height: string;
    switch (entityTitle) {
      case 'Home': {
        height = isMobileScreen ? '236px' : '385px';
        break;
      }
      case 'NotifierApp': {
        height = isMobileScreen ? '' : '182px';
        break;
      }
      default: {
        height = '';
      }
    }

    return (
      <StyledHeader
        id="header"
        role="banner"
        hasStickyTopBar={hasStickyTopBar}
        inView={inView}
        gradientColor={gradientColor}
        withVerticalBackground={withVerticalBackground}
        height={height}
      >
        <LoadingBarWrapper>
          <LoadingBar
            loading={loading}
            delay={500}
            gradientColor="rgba(255,255,255,0.15)"
          />
        </LoadingBarWrapper>
        {children}
      </StyledHeader>
    );
  }
}

export default Header;
